import moment from 'moment-timezone';
import messages from '../../../../text/messages';
import { isTrue } from '../../../../utilities/string';
import { abbrDateYear } from '../../../../utilities/dateTime';

const circleNone = { color: 'empty', full: false };

export default class Circles {
    static get1st({ onboarding: { invited, login, invite_sent_at, invite_bounced_at, invite_delivered_at }, job_timezone, created_at }) {
        const invitedAt = moment.tz(invite_sent_at ? invite_sent_at : created_at, job_timezone);

        if (isTrue(login)) {
            return { color: 'green', full: true, text: messages.acceptedInvitation };
        }
        if (isTrue(invited)) {
            if (invite_bounced_at) {
                return { color: 'red', full: true, text: messages.invitationBounced };
            }

            if (!invite_delivered_at) {
                if (hoursHaveElapsed(invitedAt, 1)) {
                    return { color: 'red', full: false, text: messages.invitationNotDelivered };
                } else {
                    return { color: 'yellow', full: false, text: messages.invitationSent };
                }
            }

            if (hoursHaveElapsed(invitedAt, 24)) {
                return { color: 'yellow', full: true, text: messages.notYetAcceptedInvitation };
            }
            return { color: 'green', full: false, text: messages.invitationDelivered };
        }

        return circleNone;
    }

    static get4th({ status, start_date, job_timezone }, circle3 = null) {
        const now = moment.tz(job_timezone);
        const startDate = moment.tz(start_date, job_timezone);

        const circle3ok = !circle3 || (circle3.color === 'green' && circle3.full);

        if (!circle3ok) {
            return circleNone;
        }

        if (status === 'active' && now >= startDate) {
            return { color: 'green', full: true, text: messages.active };
        }
        if (status === 'active' && now < startDate) {
            return { color: 'green-pat', full: true, text: messages.employeeApprovedWaitingStart };
        }
        if (status === 'rejected') {
            return { color: 'red', full: true, text: messages.canceled };
        }
        if (status === 'submitted') {
            return { color: 'green', full: false, text: messages.employeeApprovedWaitingApproval };
        }

        return circleNone;
    }

    static build(worker) {
        const { onboarding, progress } = worker;
        const { ic_path, w2_path } = onboarding || {};
        const { branch } = progress || {};

        let circle23Calculator;

        if (branch === 'dont-qualify-ic') {
            circle23Calculator = ContractorDontQualifyCircles;
        } else if (ic_path) {
            circle23Calculator = ContractorCircles;
        } else if (w2_path) {
            circle23Calculator = EmployeeCircles;
        } else {
            return [Circles.get1st(worker), circleNone, circleNone, circleNone];
        }

        const [circle2, circle3] = new circle23Calculator(worker).build();

        return [Circles.get1st(worker), circle2, circle3, Circles.get4th(worker)];
    }
}

class ContractorCircles {
    worker;
    constructor(worker) {
        this.worker = worker;
    }

    build() {
        const circle2 = this.get2nd();
        const circle3 = circle2.color === 'empty' ? circleNone : this.get3rd();
        return [circle2, circle3];
    }
    get2nd() {
        const { onboarding } = this.worker;
        const { ic_path, wizard_submitted_at, wizard_started_at } = onboarding || {};
        const { ic_docs } = ic_path || {};
        const wizardSubmittedAt = wizard_submitted_at
            ? moment(wizard_submitted_at)
            : moment(wizard_started_at)
                  .clone()
                  .add(12, 'hours');

        if (ic_docs === 'approved') {
            return { color: 'green', full: true, text: messages.icDocsApproved };
        }
        if (ic_docs === 'submitted') {
            return { color: 'green', full: false, text: messages.icDocsReviewing };
        }
        if (ic_docs === 'rejected') {
            return { color: 'red', full: true, text: messages.icDocsRejected };
        }

        if (!ic_docs || ic_docs === 'notSubmitted') {
            if (hoursHaveElapsed(wizardSubmittedAt, 60)) {
                return { color: 'red', full: true, text: messages.icDocsMissing };
            } else if (hoursHaveElapsed(wizardSubmittedAt, 24)) {
                return { color: 'yellow', full: true, text: messages.icDocsNotSubmitted };
            } else {
                return { ...circleNone, text: messages.icDocsAwaited };
            }
        }

        return circleNone;
    }
    get3rd() {
        const { ic_first_approved_at, onboarding } = this.worker;
        const { wizard, ic_path } = onboarding || {};
        const { second_onboarding_started, ic_docs } = ic_path || {};

        if (wizard === 'submitted') {
            return { color: 'green', full: true, text: { ...messages.onboardingComplete, values: { type: 'IC' } } };
        }

        if (ic_docs === 'submitted') {
            return { ...circleNone, text: messages.onboardingNotStarted };
        }

        if (ic_first_approved_at) {
            const approvedAt = moment(ic_first_approved_at);
            if (hoursHaveElapsed(approvedAt, 60)) {
                return {
                    color: 'red',
                    full: true,
                    text: { ...messages.onboardingStalled, values: { inviteAcceptedAt: abbrDateYear(approvedAt) } }
                };
            }

            if (hoursHaveElapsed(approvedAt, 24)) {
                return {
                    color: 'yellow',
                    full: true,
                    text: { ...messages.onboardingDelayed, values: { inviteAcceptedAt: abbrDateYear(approvedAt) } }
                };
            }
        }

        if (second_onboarding_started) {
            return { color: 'green', full: false, text: messages.onboardingInProcess };
        }

        return circleNone;
    }
}

class ContractorDontQualifyCircles {
    worker;
    constructor(worker) {
        this.worker = worker;
    }

    build() {
        return [this.get2nd(), this.get3rd()];
    }

    get2nd() {
        return { color: 'red', full: false, text: messages.choseDontQualify };
    }

    get3rd() {
        return circleNone;
    }
}

class EmployeeCircles {
    worker;
    constructor(worker) {
        this.worker = worker;
    }

    build() {
        return [this.get2nd(), this.get3rd()];
    }
    get2nd() {
        const { job_timezone, created_at, onboarding } = this.worker;
        const { wizard, invite_sent_at, invite_accepted_at } = onboarding || {};

        const invitedAt = moment.tz(invite_sent_at ? invite_sent_at : created_at, job_timezone).add(12, 'hours');
        const inviteAcceptedAt = moment.tz(invite_accepted_at ? invite_accepted_at : invitedAt, job_timezone);
        if (wizard === 'submitted') {
            return { color: 'green', full: true, text: { ...messages.onboardingComplete, values: { type: 'W2' } } };
        }
        if (wizard === 'notStarted') {
            return { color: 'empty', full: false, text: messages.onboardingNotStarted };
        }

        if (wizard === 'started') {
            return onboardingStartedCircle(inviteAcceptedAt);
        }

        return circleNone;
    }

    get3rd() {
        const { onboarding } = this.worker;
        const { w2_path } = onboarding;
        const { background_check } = w2_path || {};

        switch (background_check) {
            case 'approved':
                return { color: 'green', full: true, text: messages.employeeBGCheckApproved };

            case 'submitted':
            case 'pending':
                return { color: 'green', full: false, text: messages.employeeBGCheckInProcess };

            case 'notRequired':
                return { color: 'green', full: true, text: messages.employeeBGCheckNotRequired };

            case 'inReview':
            case 'suspended':
                return { color: 'yellow', full: true, text: messages.employeeBGCheckUnderReview };

            case 'rejected':
                return { color: 'red', full: true, text: messages.employeeBGCheckException };

            case 'notStarted':
            default:
                return { ...circleNone, text: messages.employeeBGCheckNotYetStarted };
        }
    }
}

function onboardingStartedCircle(inviteAcceptedAt) {
    if (hoursHaveElapsed(inviteAcceptedAt, 60)) {
        return {
            color: 'red',
            full: true,
            text: { ...messages.onboardingStalled, values: { inviteAcceptedAt: abbrDateYear(inviteAcceptedAt) } }
        };
    } else if (hoursHaveElapsed(inviteAcceptedAt, 24)) {
        return {
            color: 'yellow',
            full: true,
            text: { ...messages.onboardingDelayed, values: { inviteAcceptedAt: abbrDateYear(inviteAcceptedAt) } }
        };
    } else {
        return { color: 'green', full: false, text: messages.onboardingInProcess };
    }
}

function hoursHaveElapsed(timerStart, hours) {
    const now = moment();

    const result = now > timerStart.clone().add(hours, 'hours');
    return result;
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/components/workers/circles.jsx